import { LucideIcon } from 'lucide-react';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface MetricCardProps {
    label: string;
    value: string | number;
    icon: LucideIcon;
    theme?: 'primary' | 'success' | 'warning' | 'info' | 'purple';
    trend?: {
        value: number;
        isPositive: boolean;
    };
    details?: Array<{ key: string; val: string | number }>;
    className?: string;
    featured?: boolean;
}

const themeStyles = {
    primary: {
        bg: 'bg-blue-500',
        text: 'text-blue-600',
        lightBg: 'bg-blue-50',
        border: 'border-blue-100'
    },
    success: {
        bg: 'bg-emerald-500',
        text: 'text-emerald-600',
        lightBg: 'bg-emerald-50',
        border: 'border-emerald-100'
    },
    warning: {
        bg: 'bg-amber-500',
        text: 'text-amber-600',
        lightBg: 'bg-amber-50',
        border: 'border-amber-100'
    },
    info: {
        bg: 'bg-cyan-500',
        text: 'text-cyan-600',
        lightBg: 'bg-cyan-50',
        border: 'border-cyan-100'
    },
    purple: {
        bg: 'bg-purple-500',
        text: 'text-purple-600',
        lightBg: 'bg-purple-50',
        border: 'border-purple-100'
    }
};

export function MetricCard({
                               label,
                               value,
                               icon: Icon,
                               theme = 'primary',
                               trend,
                               details,
                               className = '',
                               featured = false,
                           }: MetricCardProps) {
    const styles = themeStyles[theme];

    return (
        <div
            className={`relative overflow-hidden rounded-xl border ${styles.border} bg-white ${
                featured ? 'p-6 sm:p-8' : 'p-5'
            } hover:shadow-lg transition-all duration-300 group h-full flex flex-col ${className}`}
        >
            {/* Background decorativo */}
            <div
                className={`absolute top-0 right-0 ${
                    featured ? 'w-64 h-64' : 'w-32 h-32'
                } ${styles.lightBg} rounded-full blur-3xl opacity-30 -mr-16 -mt-16 group-hover:opacity-50 transition-opacity`}
            />

            <div className="relative z-10 flex-1 flex flex-col">
                {/* Header com ícone */}
                <div className={`flex items-center justify-between ${featured ? 'mb-6' : 'mb-4'}`}>
                    <div className={`${featured ? 'p-3.5' : 'p-2.5'} rounded-lg ${styles.lightBg}`}>
                        <Icon className={`${featured ? 'h-7 w-7' : 'h-5 w-5'} ${styles.text}`} />
                    </div>
                    {trend && (
                        <div className={`flex items-center gap-1 text-xs font-semibold ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                            {trend.isPositive ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                            {Math.abs(trend.value)}%
                        </div>
                    )}
                </div>

                {/* Label */}
                <h3 className={`${featured ? 'text-base' : 'text-sm'} font-medium text-gray-600 mb-1`}>
                    {label}
                </h3>

                {/* Valor principal */}
                <p
                    className={`${
                        featured ? 'text-4xl sm:text-5xl' : 'text-2xl'
                    } font-bold text-gray-900 mb-3`}
                >
                    {value}
                </p>

                {/* Detalhes adicionais */}
                {details && details.length > 0 && (
                    <div className={`${featured ? 'mt-auto' : ''} pt-3 border-t border-gray-100 space-y-1.5`}>
                        {details.map((detail, idx) => (
                            <div
                                key={idx}
                                className={`flex items-center justify-between ${featured ? 'text-sm' : 'text-xs'}`}
                            >
                                <span className="text-gray-500">{detail.key}</span>
                                <span className="font-semibold text-gray-700">{detail.val}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}